import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { GET_LINKED_EXTERNAL_ACCOUNTS } from '@/src/common/gql/queries/card'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useCloseLinkedExternalBankAccount } from '@/src/common/hooks/useCloseLinkedExternalBankAccount'
import { parseLinkedExternalBankAccounts } from '@/src/app/main/(tabs)/card/card-management/_common/AccountSection'
import type { PaymentMethodsComponent } from '@/src/experiences/card-payments/providePaymentMethods'
import {
  useLegacyCompletePlaidLink,
  type LegacyCompletePlaidLinkProps,
} from '@/src/common/hooks/usePlaidLink/useCompletePlaidLink/useLegacyCompletePlaidLink'
import { usePlaidLink } from '@/src/common/hooks/usePlaidLink'
import * as MesaLib from '@acre-rewards/lib'
import { type ComponentProps, useCallback, useMemo } from 'react'
import { convertMesaLibProductsToLegacyProducts } from '@/src/common/hooks/usePlaidLink/usePlaidLinkBase'

interface Props extends LegacyCompletePlaidLinkProps {
  ToWrap: PaymentMethodsComponent
  products: typeof MesaLib.Plaid.Products.options
}

export function LegacyPaymentMethods({ ToWrap, products, ...props }: Props) {
  const onError = useLogError()
  const { data, loading: loadingAccounts } = useLoggedInQuery(GET_LINKED_EXTERNAL_ACCOUNTS, {
    variables: { products: convertMesaLibProductsToLegacyProducts(products) },
    fetchPolicy: 'cache-and-network',
    onError,
  })

  const [closeLinkedExternalAccount, { loading: removingAccountLoading }] =
    useCloseLinkedExternalBankAccount()

  const { openPlaidLink, loading } = usePlaidLink(products, useLegacyCompletePlaidLink(props))

  return (
    <ToWrap
      data={useMemo(() => (data ? parseLinkedExternalBankAccounts(data) : undefined), [data])}
      loading={loadingAccounts}
      closePaymentMethod={useCallback<
        NonNullable<ComponentProps<typeof ToWrap>['closePaymentMethod']>
      >(
        (paymentMethod) => {
          void closeLinkedExternalAccount({
            variables: {
              highnoteExternalAccountId: paymentMethod.highnoteExternalAccountId,
              plaidAccountId: paymentMethod.plaidAccountId,
            },
          })
        },
        [closeLinkedExternalAccount],
      )}
      closingPaymentMethod={removingAccountLoading}
      openPlaidLink={useCallback<NonNullable<ComponentProps<typeof ToWrap>['openPlaidLink']>>(
        (paymentMethod) => {
          if (!paymentMethod) {
            return openPlaidLink()
          }

          if (paymentMethod.plaidAccountId) {
            return openPlaidLink({ accountId: paymentMethod.plaidAccountId })
          }
        },
        [openPlaidLink],
      )}
      plaidLinkLoading={loading}
    />
  )
}
